import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bookmark, GripVertical, ArrowUpDown } from "lucide-react";
import { Reorder } from "framer-motion";
import Navbar from "../components/Navbar";
import PosterCard from "../components/PosterCard";
import Avatar from "../components/Avatar";
import WheelBrowser, { cardRow } from "../components/clickwheel/WheelBrowser";
import api from "../lib/api";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { useTheme } from "../context/ThemeContext";

function RentalReceipt({ items, user, navigate }) {
  const now = new Date();
  const date = now.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "2-digit" });
  const time = now.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
  const nextFriday = (() => {
    const d = new Date();
    d.setDate(d.getDate() + ((5 - d.getDay() + 7) % 7 || 7));
    return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
  })();
  const barnum = String(Math.abs([...(user?.id || user?.email || "dial")].reduce((a, c) => a + c.charCodeAt(0), 0) * 7919)).padStart(12, "0").slice(0, 12);
  return (
    <div className="vs-receipt" data-testid="my-list-receipt">
      <div className="vs-receipt-head">
        <p className="vs-receipt-brand">THE DIAL VIDEO</p>
        <p className="vs-receipt-sub">Rental Receipt</p>
      </div>
      <p className="vs-receipt-meta">{date} · {time}<br />MEMBER: {(user?.name || user?.email || "GUEST").toUpperCase()}</p>
      <div className="vs-receipt-rule" />
      <div className="vs-receipt-cols"><span>Qty · Title</span><span>Year</span></div>
      <div className="vs-receipt-rule dashed" />
      {items.map((it, i) => (
        <button key={it.id} className="vs-receipt-line" onClick={() => navigate(`/title/${it.id}`)} data-testid={`receipt-line-${it.id}`}>
          <span className="idx">{String(i + 1).padStart(2, "0")}.</span>
          <span className="name">{it.title}</span>
          <span className="leader" />
          <span className="yr">{it.year || "—"}</span>
        </button>
      ))}
      <div className="vs-receipt-rule dashed" />
      <div className="vs-receipt-total"><span>TOTAL RENTALS</span><span>{items.length}</span></div>
      <div className="vs-receipt-rule" />
      <p className="vs-receipt-thanks">★ Thank You — Enjoy The Show ★</p>
      <div className="vs-receipt-barcode" />
      <p className="vs-receipt-barnum">{barnum}</p>
      <div className="vs-receipt-stamp" data-testid="receipt-overdue-stamp" aria-hidden="true">
        <span className="big">Due Back Fri {nextFriday}</span>
        <span className="small">Please Rewind</span>
      </div>
    </div>
  );
}

export default function MyList() {
  const navigate = useNavigate();
  const { user, patchUser } = useAuth();
  const { skin } = useTheme();
  const videostore = skin === "videostore";
  const wheel = skin === "clickwheel";
  const [items, setItems] = useState(null);
  const [editing, setEditing] = useState(false);
  const [order, setOrder] = useState([]);
  const [savingOrder, setSavingOrder] = useState(false);

  const load = () => api.get("/watchlist").then(({ data }) => setItems(data.items)).catch(() => setItems([]));

  useEffect(() => { load(); }, [user?.watchlist?.length]);

  const startEdit = () => { setOrder(items); setEditing(true); };
  const cancelEdit = () => { setEditing(false); };

  // Click Wheel: move the selected title up/down and save right away.
  const moveItem = async (i, dir) => {
    const next = [...items];
    const [it] = next.splice(i, 1);
    next.splice(i + dir, 0, it);
    setItems(next);
    const ids = next.map((x) => x.id);
    try { await api.put("/watchlist/order", { ids }); patchUser({ watchlist: ids }); }
    catch { toast.error("Could not save the new order."); load(); }
  };

  const saveOrder = async () => {
    setSavingOrder(true);
    const ids = order.map((i) => i.id);
    try {
      await api.put("/watchlist/order", { ids });
      setItems(order);
      patchUser({ watchlist: ids });
      toast.success("Order saved");
      setEditing(false);
    } catch {
      toast.error("Could not save the new order.");
    } finally {
      setSavingOrder(false);
    }
  };

  return (
    <div className="min-h-screen bg-[var(--bg-base)]">
      <Navbar />
      <main className="px-4 lg:px-14 pt-6 pb-20" data-testid="my-list-page">
        {!videostore && (
          <div className="flex flex-wrap items-center justify-between gap-3" data-testid="my-list-header">
            <div className="flex items-center gap-3">
              <Avatar user={user} size={48} className="flex-none border border-[var(--border-subtle)]" testid="my-list-avatar" />
              <div>
                <h1 className="font-head text-4xl sm:text-5xl tracking-wide text-[var(--text-primary)]">{user?.name ? `${user.name}'s List` : "My List"}</h1>
                <p className="mt-1 text-[var(--text-secondary)]">Your universal watchlist across every service.</p>
              </div>
            </div>
            {items && items.length > 1 && !editing && !wheel && (
              <button onClick={startEdit} className="inline-flex items-center gap-2 rounded-[var(--card-radius)] border border-[var(--border-subtle)] bg-[var(--bg-card)] px-4 py-2 text-sm font-semibold text-[var(--text-primary)] hover:border-[var(--accent)]" data-testid="my-list-edit-order">
                <ArrowUpDown size={16} /> Edit order
              </button>
            )}
          </div>
        )}

        {videostore && items && items.length > 1 && !editing && (
          <div className="mb-4 flex justify-end">
            <button onClick={startEdit} className="inline-flex items-center gap-2 rounded-[var(--card-radius)] border border-[var(--border-subtle)] bg-[var(--bg-card)] px-4 py-2 text-sm font-semibold text-[var(--text-primary)] hover:border-[var(--accent)]" data-testid="my-list-edit-order">
              <ArrowUpDown size={16} /> Edit order
            </button>
          </div>
        )}

        {items === null && <p className="mt-6 text-[var(--text-secondary)]">Loading…</p>}

        {items && items.length === 0 && (
          <div className="mt-20 flex flex-col items-center text-center text-[var(--text-secondary)]" data-testid="my-list-empty">
            <Bookmark size={44} className="mb-4 opacity-50" />
            <p className="text-lg">Your list is empty.</p>
            <button onClick={() => navigate("/")} className="mt-5 rounded-md bg-[var(--accent)] px-6 py-2.5 font-bold uppercase tracking-wide text-white hover:bg-[var(--accent-hover)]" data-testid="my-list-browse-button">
              Browse titles
            </button>
          </div>
        )}

        {items && items.length > 0 && (
          editing ? (
            <div className="mt-6" data-testid="my-list-reorder">
              <div className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded-[var(--card-radius)] border border-[var(--border-accent)] bg-[var(--accent)]/10 px-4 py-3">
                <p className="text-sm text-[var(--text-primary)]">Drag the titles into the order you want.</p>
                <div className="flex items-center gap-2">
                  <button onClick={saveOrder} disabled={savingOrder} className="rounded-[var(--card-radius)] bg-[var(--accent)] px-5 py-2 text-sm font-bold uppercase tracking-wide text-[var(--accent-contrast)] hover:bg-[var(--accent-hover)] disabled:opacity-60" data-testid="my-list-save-order">
                    {savingOrder ? "Saving…" : "Save order"}
                  </button>
                  <button onClick={cancelEdit} disabled={savingOrder} className="rounded-[var(--card-radius)] border border-[var(--border-subtle)] px-4 py-2 text-sm text-[var(--text-secondary)] hover:text-[var(--text-primary)] disabled:opacity-60" data-testid="my-list-cancel-order">
                    Cancel
                  </button>
                </div>
              </div>
              <Reorder.Group axis="y" values={order} onReorder={setOrder} className="mx-auto max-w-2xl space-y-2">
                {order.map((it, i) => (
                  <Reorder.Item
                    key={it.id}
                    value={it}
                    className="flex cursor-grab items-center gap-3 rounded-[var(--card-radius)] border border-[var(--border-subtle)] bg-[var(--bg-card)] p-2.5 active:cursor-grabbing"
                    whileDrag={{ scale: 1.02, boxShadow: "0 8px 24px rgba(0,0,0,0.35)" }}
                    data-testid={`reorder-item-${it.id}`}
                  >
                    <GripVertical size={18} className="flex-none text-[var(--text-muted)]" data-testid={`reorder-handle-${it.id}`} />
                    <span className="w-6 flex-none text-center text-sm font-bold text-[var(--text-muted)]">{i + 1}</span>
                    <img src={it.poster || it.img} alt="" onError={(e) => { e.currentTarget.src = it.img; }} className="h-16 w-11 flex-none rounded object-cover" />
                    <div className="min-w-0 flex-1">
                      <p className="truncate font-semibold text-[var(--text-primary)]">{it.title}</p>
                      <p className="text-xs text-[var(--text-secondary)]">{it.year || "—"}</p>
                    </div>
                  </Reorder.Item>
                ))}
              </Reorder.Group>
            </div>
          ) : videostore ? (
            <RentalReceipt items={items} user={user} navigate={navigate} />
          ) : wheel ? (
            <WheelBrowser rows={items.map((it) => cardRow(it))} title="My List" testid="wheel-my-list" onMove={moveItem} />
          ) : (
            <div className="mt-8 grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6" data-testid="my-list-grid">
              {items.map((it) => (
                <div key={it.id} className="w-full [&>div]:w-full">
                  <PosterCard item={it} />
                </div>
              ))}
            </div>
          )
        )}
      </main>
    </div>
  );
}
